import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AlertListService} from '@xref/ngx';

import Constants from '../../shared/constants';
import {PermissionsService} from '../permissions/permissions.service';
import {SharedService} from '../shared/shared.service';
import {MFA_FEATURE_FLAG} from '../two-factor/two-factor.service';

export const SIGNUP_MFA_ATTEMPTS_EXCEEDED = 'SIGNUP_MFA_ATTEMPTS_EXCEEDED';

/**
 * After Enterprise MFA fails too many times during existing-user organisation
 * signup, send the user back through login (user-verify) and keep signup
 * progress so they can continue after a successful login (ENT-11812).
 */
@Injectable({providedIn: 'root'})
export class SignupMfaLockoutService {
    constructor(
        private alertList: AlertListService,
        private router: Router,
        private sharedService: SharedService,
        private permissions: PermissionsService,
    ) {}

    /**
     * @returns true when the error was handled as a signup MFA hard stop.
     */
    handleIfSignupMfaExhausted(err: any): boolean {
        if (!this.isMfaFeatureActive()
            || !this.isSignupMfaInProgress()
            || !this.isAttemptsExceeded(err)) {
            return false;
        }

        const canResumeSignup = this.prepareReturnToLogin();
        this.alertList.append([{
            active: true,
            type: 'danger',
            messageKey: 'twoFactor.alerts.signupMfaAttemptsExceeded',
            keepOnRouteChange: true,
        }]);
        this.router.navigate([canResumeSignup ? '/user-verify' : '/']);
        return true;
    }

    private isMfaFeatureActive(): boolean {
        return this.permissions.isFeatureActivated(MFA_FEATURE_FLAG);
    }

    private isSignupMfaInProgress(): boolean {
        return sessionStorage.getItem(Constants.SIGNUP_MFA_IN_PROGRESS) === 'true';
    }

    private isAttemptsExceeded(err: any): boolean {
        return this.getOtpErrorCode(err) === SIGNUP_MFA_ATTEMPTS_EXCEEDED;
    }

    private getOtpErrorCode(err: any): string | null {
        if (!err || !err.error || !err.error.data || !err.error.data.details) {
            return null;
        }
        return err.error.data.details.code || null;
    }

    /**
     * Clear only the MFA-in-progress marker. Rebuild signupUserData from the
     * in-memory signup payload so user-verify can show the same person and
     * resume org creation after password + MFA succeed again.
     *
     * @returns true when there is a signup to resume at /user-verify. Users who
     * arrived from a fresh registration (signup.xref.com) have no payload here,
     * and UserGuard would bounce them out of /user-verify, losing the alert.
     */
    private prepareReturnToLogin(): boolean {
        sessionStorage.removeItem(Constants.SIGNUP_MFA_IN_PROGRESS);
        // Always drop any prior snapshot first so incomplete restore data cannot
        // leave another user's email/uuid/token in place.
        localStorage.removeItem('signupUserData');

        const userData = this.sharedService.getUserData() || {};
        const email = userData.email;
        const token = userData.token;
        const uuid = userData.uuid;
        const firstName = userData.firstName || userData.first_name || '';
        const lastName = userData.lastName || userData.last_name || '';

        if (!(email && token && uuid)) {
            localStorage.removeItem('previousUrl');
            return false;
        }

        localStorage.setItem('signupUserData', JSON.stringify({
            email,
            firstName,
            lastName,
            uuid,
            token,
            _expires: Date.now() + (30 * 60 * 1000),
        }));
        localStorage.setItem('previousUrl', '/user-verify');
        return true;
    }
}
